import api from "../api/axiosInstance";

// Every resume file in this app is served by an authenticated
// res.download(...) endpoint (Content-Disposition: attachment), which is
// correct for an explicit "Download" button but means a plain <a href>
// or window.open(url) to that same endpoint just saves the file to disk
// instead of opening it.
//
// PDFs render natively in a browser tab once that Content-Disposition
// header is out of the way (fetch as a blob, point the tab at a blob:
// URL built from the blob's own Content-Type instead of the original
// response) — that's the PDF branch below.
//
// DOCX is different: no browser has a native inline renderer for it, so
// even with a perfectly clean blob: URL and the right MIME type, "opening"
// one in a tab just triggers ANOTHER download (or a blank tab, depending
// on the browser) — that was this function's actual bug, not a header
// problem. The fix is to actually render it: mammoth.js converts the
// .docx's own XML into HTML entirely client-side (no server changes, no
// LibreOffice/Office-online dependency), which gets written straight into
// the already-open tab.
//
// The tab is opened synchronously, before the request resolves, so
// browsers don't treat it as an unrequested popup and block it; its
// content is filled in once the file has actually loaded/converted.
const DOCX_CONTENT_TYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

function writeIntoTab(tab, bodyHtml, title) {
  if (!tab) return;
  tab.document.open();
  tab.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${title}</title>
    <style>
      body { font-family: Calibri, Arial, Helvetica, sans-serif; max-width: 800px; margin: 40px auto; padding: 0 24px 60px; line-height: 1.5; color: #1a1a1a; }
      h1, h2, h3 { margin-top: 1.3em; margin-bottom: 0.4em; }
      p { margin: 0.5em 0; }
      ul, ol { margin: 0.5em 0; padding-left: 1.5em; }
      .preview-notice { background: #fff8e1; color: #8a6d00; padding: 10px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 24px; }
    </style>
  </head>
  <body>${bodyHtml}</body>
</html>`);
  tab.document.close();
}

export async function viewFileInNewTab(url, { onError } = {}) {
  const newTab = window.open("", "_blank");

  try {
    const res = await api.get(url, { responseType: "arraybuffer" });
    const contentType = res.headers?.["content-type"] || "application/octet-stream";

    if (contentType.includes("pdf")) {
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: contentType }));
      if (newTab) {
        newTab.location.href = blobUrl;
      } else {
        // Popup was blocked before we even had the file — try once more
        // now that we have a real blob URL (may still be blocked, but
        // this is the best fallback available).
        window.open(blobUrl, "_blank");
      }
      return;
    }

    if (contentType.includes(DOCX_CONTENT_TYPE)) {
      const mammothModule = await import("mammoth");
      const mammoth = mammothModule.default || mammothModule;
      const { value: html, messages } = await mammoth.convertToHtml({ arrayBuffer: res.data });

      // mammoth does a best-effort conversion — it can't reproduce exact
      // page layout/fonts, only structure and text (headings, bullets,
      // bold/italic, etc.), so a genuine layout question still needs
      // Download. Surfaced as a small notice rather than silently, so it
      // doesn't read as a broken/incomplete render.
      const notice =
        '<div class="preview-notice">This is a text preview of the resume\'s content — formatting may differ from the actual file. Use Download for the exact document.</div>';
      writeIntoTab(newTab, notice + html, "Resume Preview");
      if (messages && messages.length) {
        console.warn("mammoth conversion notes:", messages);
      }
      return;
    }

    // Anything else (old binary .doc, etc.) has no in-browser preview
    // available — say so instead of leaving a blank tab while a
    // surprise download starts in the background.
    writeIntoTab(
      newTab,
      "<p>Preview isn't available for this file type. Use Download instead to open it in Word.</p>",
      "Preview unavailable"
    );
  } catch (err) {
    if (newTab) newTab.close();
    if (onError) onError(err);
    else console.error("Failed to open file:", err);
  }
}

// Downloads a file from an authenticated endpoint straight to disk (as
// opposed to viewFileInNewTab above, which opens/renders it inline). Same
// blob fetch, but triggered via a throwaway <a download> instead of a new
// tab — always the raw original file, never the mammoth-rendered preview.
export async function downloadFile(url, fileName, { onError } = {}) {
  try {
    const res = await api.get(url, { responseType: "blob" });
    const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = blobUrl;
    link.setAttribute("download", fileName || "resume");
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
  } catch (err) {
    if (onError) onError(err);
    else console.error("Failed to download file:", err);
  }
}
